import type {Metadata} from "@khanacademy/wonder-stuff-core";

import type {
    AmbiguousError,
    ErrorKind,
    IAmbiguousErrorLog,
    LogErrorOptions,
} from "./types.ts";

/**
 * Lazily instantiated log.
 *
 * Provides a log implementation that defers to lazily created log provided
 * by a given log building delegate. This way, we can provide the log but
 * it will only actually be a real log when something first attempts to log
 * to it.
 */
export class LazyAmbiguousErrorLog implements IAmbiguousErrorLog {
    _builder: () => IAmbiguousErrorLog;
    _manifestedLog: IAmbiguousErrorLog | null | undefined;

    constructor(logBuilder: () => IAmbiguousErrorLog) {
        this._builder = logBuilder;
    }

    _log: () => IAmbiguousErrorLog = () => {
        if (this._manifestedLog == null) {
            this._manifestedLog = this._builder();
        }
        return this._manifestedLog;
    };

    log: (
        message: string,
        metadata?: Metadata | null | undefined,
        ...args: Array<unknown>
    ) => void = (message, metadata, ...args): void =>
        this._log().log(message, metadata, ...args);

    error: (
        error: AmbiguousError,
        maybeKind?: ErrorKind | null | undefined,
        maybeOptions?: LogErrorOptions | null | undefined,
    ) => void = (...args) => {
        // This method is overloaded and writing code to deal with the
        // different overloads just to get types right isn't worth it,
        // so we cast the log interface to any, hiding the errors.
        return (this._log() as any).error(...args);
    };
}
